/**
 * Processes question set data and handles both old and new formats
 * Old format: flat array of questions
 * New format: items array with levels, each containing multiple questions
 */

/**
 * Randomly selects one question from each level
 * @param {Array} items - Array of level objects with questions
 * @returns {Array} - Flat array of randomly selected questions
 */
const selectRandomQuestionsFromLevels = (items) => {
  const selectedQuestions = [];
  
  items.forEach((levelObj) => {
    const { level, questions } = levelObj;
    
    if (questions && questions.length > 0) {
      // Randomly select one question from this level
      const randomIndex = Math.floor(Math.random() * questions.length);
      const selectedQuestion = { ...questions[randomIndex] };
      
      // Assign a sequential ID based on the level
      selectedQuestion.id = level;
      
      selectedQuestions.push(selectedQuestion);
    }
  });
  
  return selectedQuestions;
};

/**
 * Randomly selects one extra question from the extraQuestions array
 * @param {Array|Object} extraQuestions - Array of extra questions or single extra question object
 * @returns {Object|null} - Randomly selected extra question or null
 */
const selectRandomExtraQuestion = (extraQuestions) => {
  // Handle old format (single extraQuestion object)
  if (extraQuestions && !Array.isArray(extraQuestions)) {
    return extraQuestions;
  }
  
  // Handle new format (array of extraQuestions)
  if (Array.isArray(extraQuestions) && extraQuestions.length > 0) {
    const randomIndex = Math.floor(Math.random() * extraQuestions.length);
    return extraQuestions[randomIndex];
  }
  
  return null;
};

/**
 * Processes a question set and returns a standardized format
 * Detects whether it's the old format (flat questions) or new format (items with levels)
 * @param {Object} questionSetData - The raw question set data from JSON
 * @returns {Object} - Processed question set with flat questions array
 */
export const processQuestionSet = (questionSetData) => {
  if (!questionSetData) return null;
  
  // Check if this is the new format (has 'items' array)
  if (questionSetData.items && Array.isArray(questionSetData.items)) {
    // New format: randomly select questions from levels
    const randomQuestions = selectRandomQuestionsFromLevels(questionSetData.items);
    
    // Keep the entire array of extra questions for the lifeline logic in App.jsx
    const extraQuestions = questionSetData.extraQuestions || (questionSetData.extraQuestion ? [questionSetData.extraQuestion] : []);

    return {
      ...questionSetData,
      questions: randomQuestions,
      extraQuestions: extraQuestions, // Pass the whole array
      // Keep items and original extra questions for reference
      _originalItems: questionSetData.items,
      _originalExtraQuestions: questionSetData.extraQuestions || questionSetData.extraQuestion
    };
  }
  
  // Old format: already has flat questions array
  // Still process extra questions if they're an array
  const extraQuestion = selectRandomExtraQuestion(
    questionSetData.extraQuestions || questionSetData.extraQuestion
  );
  
  return {
    ...questionSetData,
    extraQuestion: extraQuestion,
    _originalExtraQuestions: questionSetData.extraQuestions || questionSetData.extraQuestion
  };
};

/**
 * Re-randomizes questions for a question set (used when playing again)
 * @param {Object} questionSetData - The question set data
 * @returns {Object} - New question set with re-randomized questions
 */
export const rerandomizeQuestionSet = (questionSetData) => {
  if (!questionSetData) return null;
  
  // If it has _originalItems, it was processed from the new format
  if (questionSetData._originalItems) {
    const randomQuestions = selectRandomQuestionsFromLevels(questionSetData._originalItems);
    
    // Re-randomize extra question as well
    const extraQuestion = selectRandomExtraQuestion(questionSetData._originalExtraQuestions);
    
    return {
      ...questionSetData,
      questions: randomQuestions,
      extraQuestion: extraQuestion
    };
  }
  
  // Old format - still re-randomize extra question if available
  if (questionSetData._originalExtraQuestions) {
    const extraQuestion = selectRandomExtraQuestion(questionSetData._originalExtraQuestions);
    return {
      ...questionSetData,
      extraQuestion: extraQuestion
    };
  }
  
  return questionSetData;
};

const questionProcessorExports = {
  processQuestionSet,
  rerandomizeQuestionSet
};

export default questionProcessorExports;

